import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { TrendingUp, Users, Globe, Coins } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
}

interface DashboardOverviewProps {
  company: Company | null;
}

interface RemoteWorkData {
  month: string;
  count: number;
}

interface KPIData {
  employeesAbroad: number;
  teleworkPercentage: number;
  totalPointsUsed: number;
  monthlyIncrease: number;
}

const DashboardOverview = ({ company }: DashboardOverviewProps) => {
  const [chartData, setChartData] = useState<RemoteWorkData[]>([]);
  const [kpiData, setKPIData] = useState<KPIData>({
    employeesAbroad: 0,
    teleworkPercentage: 0,
    totalPointsUsed: 0,
    monthlyIncrease: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (company) {
      fetchDashboardData();
    }
  }, [company]);

  const fetchDashboardData = async () => {
    if (!company) return;

    try {
      // Fetch remote work requests for the last 6 months
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      // Use the secure RPC function
      const { data: allRequests } = await supabase
        .rpc('get_company_remote_work_requests');

      // Filter for approved requests within date range
      const remoteRequests = (allRequests || []).filter(req => 
        req.status === 'approved' && 
        new Date(req.created_at) >= sixMonthsAgo
      ).map(req => ({
        ...req,
        employees: { company_id: company.id }
      }));

      // Process chart data
      const monthlyData: { [key: string]: number } = {};
      const months = [];
      
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthKey = date.toISOString().substring(0, 7);
        const monthLabel = date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
        months.push({ key: monthKey, label: monthLabel });
        monthlyData[monthKey] = 0;
      }

      remoteRequests?.forEach(request => {
        const monthKey = request.created_at.substring(0, 7);
        if (monthlyData[monthKey] !== undefined) {
          monthlyData[monthKey]++;
        }
      });

      const chartData = months.map(month => ({
        month: month.label,
        count: monthlyData[month.key]
      }));

      setChartData(chartData);

      // Fetch KPI data
      await fetchKPIData();

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchKPIData = async () => {
    if (!company) return;

    try {
      // Employees currently abroad (approved requests)
      // Use the secure RPC function
      const { data: allAbroadRequests } = await supabase
        .rpc('get_company_remote_work_requests');

      const todayStr = new Date().toISOString().split('T')[0];
      const abroadEmployees = (allAbroadRequests || []).filter(req =>
        req.status === 'approved' &&
        req.start_date && req.start_date <= todayStr
      ).map(req => ({
        ...req,
        employees: { company_id: company.id }
      }));

      // Total employees who have done remote work this year
      const currentYear = new Date().getFullYear();
      // Use the secure RPC function
      const { data: allTeleworkRequests } = await supabase
        .rpc('get_company_remote_work_requests');

      const teleworkEmployees = (allTeleworkRequests || []).filter(req =>
        req.status === 'approved' &&
        req.created_at && req.created_at.startsWith(currentYear.toString())
      ).map(req => ({
        ...req,
        employees: { company_id: company.id }
      }));

      // Total points used
      const { data: pointsData } = await supabase
        .from('employee_points')
        .select(`
          used_points,
          employee_id,
          employees!inner(company_id)
        `)
        .eq('employees.company_id', company.id);

      // Requests this month vs last month
      const thisMonth = new Date();
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);

      // Use the secure RPC function
      const { data: allMonthlyRequests } = await supabase
        .rpc('get_company_remote_work_requests');

      const thisMonthRequests = (allMonthlyRequests || []).filter(req =>
        req.status === 'approved' &&
        req.created_at && req.created_at >= thisMonth.toISOString().substring(0, 7) + '-01'
      );

      const lastMonthRequests = (allMonthlyRequests || []).filter(req =>
        req.status === 'approved' &&
        req.created_at && 
        req.created_at >= lastMonth.toISOString().substring(0, 7) + '-01' &&
        req.created_at < thisMonth.toISOString().substring(0, 7) + '-01'
      );

      const uniqueEmployeesAbroad = new Set(abroadEmployees?.map(r => r.employee_id)).size;
      const uniqueTeleworkEmployees = new Set(teleworkEmployees?.map(r => r.employee_id)).size;
      const totalPointsUsed = pointsData?.reduce((sum, p) => sum + p.used_points, 0) || 0;
      
      const thisMonthCount = thisMonthRequests.length;
      const lastMonthCount = lastMonthRequests.length;
      const monthlyIncrease = lastMonthCount === 0 ? 0 : 
        ((thisMonthCount - lastMonthCount) / lastMonthCount) * 100;

      setKPIData({
        employeesAbroad: uniqueEmployeesAbroad,
        teleworkPercentage: company.employee_count === 0 ? 0 : 
          Math.round((uniqueTeleworkEmployees / company.employee_count) * 100),
        totalPointsUsed,
        monthlyIncrease: Math.round(monthlyIncrease)
      });

    } catch (error) {
      console.error('Error fetching KPI data:', error);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-8 bg-muted rounded w-3/4 mb-4"></div>
                <div className="h-12 bg-muted rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="animate-pulse">
          <CardContent className="p-6">
            <div className="h-64 bg-muted rounded"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Employés à l'étranger
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {kpiData.employeesAbroad}
                </p>
              </div>
              <Globe className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  % Télétravail cette année
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {kpiData.teleworkPercentage}%
                </p>
              </div>
              <Users className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Nest Points utilisés
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {kpiData.totalPointsUsed}
                </p>
              </div>
              <Coins className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Évolution du télétravail
          </CardTitle>
          <CardDescription>
            Nombre d'employés en télétravail approuvé sur les 6 derniers mois
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  labelFormatter={(label) => `Mois: ${label}`}
                  formatter={(value) => [value, 'Employés en télétravail']}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--primary))' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Increase Card */}
      <Card className="border-l-4 border-l-orange-500">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Évolution mensuelle
              </p>
              <p className={`text-2xl font-bold ${kpiData.monthlyIncrease >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {kpiData.monthlyIncrease >= 0 ? '+' : ''}{kpiData.monthlyIncrease}%
              </p>
              <p className="text-xs text-muted-foreground">
                par rapport au mois dernier
              </p>
            </div>
            <TrendingUp className={`h-8 w-8 ${kpiData.monthlyIncrease >= 0 ? 'text-green-500' : 'text-red-500'}`} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardOverview;